/** The open fixture, learned from the operator app's OWN network traffic.
 *
 * ⛔ WHY. `DigitainSource.getMatches()` is fed by the SportFrame bus, which
 * speaks only for an IFRAME sportsbook or an operator that forwards events.
 * Parikara is native DOM and does neither, so `current_context` has been null on
 * EVERY request from that operator — confirmed 2026-08-31 from the payload
 * probe: `has_context=false` on every sampled row. The backend's 1.6
 * browsed-event boost has therefore never fired there, and a visitor sitting on
 * Lecce v Roma was shown women's tennis.
 *
 * Parikara routes in memory, so there is no event id in the URL to read. But
 * opening a match makes the APP fetch that event, and the SDK already wraps
 * `window.fetch` for bet history — so the id passes through our hands already.
 *
 * ⭐ THE SAFETY GUARD, same as the DOM reader's: this never returns an id on its
 * own authority. `openEventId(board)` returns a value only when the observed id
 * is ALSO a fixture on the board we hold, so a misparse yields null — today's
 * behaviour — rather than pointing at the wrong match.
 */

const EVENT_HINT = /GetEvent|EventDetail|GetEventById|eventId/i;
/** How long an observed id stays meaningful. A visitor who moved on should not
 *  keep boosting the fixture they left; the poll runs every 5s, so this is
 *  several polls of grace and no more. */
const FRESH_MS = 90_000;

let lastId = "";
let lastAt = 0;
let wrapped = false;

/** Pull every plausible event id out of a URL and/or request body. */
export function extractEventIds(text: string): string[] {
  if (!text) return [];
  const out: string[] = [];
  // "eventId":123456 / eventId=123456 / "Id":123456
  const re = /(?:"?\b(?:eventId|EventId|Id)"?\s*[:=]\s*"?)(\d{5,12})/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) out.push(m[1]);
  return out;
}

// ⛔ ONE slot, not a list. The host SPA rebuilds the widget per navigation and
// this module is process-wide, so a list would accumulate a closure per rebuild,
// each priming and polling on a renderer that no longer exists. The newest
// widget owns the wake-up. (`lastId` also persists across rebuilds, so a
// rebuild on the SAME match does not fire — the rebuilt widget's first poll
// resolves that match through resolveOpenMatch as before.)
let listener: ((id: string) => void) | null = null;

export function onOpenMatchObserved(cb: (id: string) => void): void {
  listener = cb;
}

export function stopOpenMatchObserved(): void {
  listener = null;
}

/** Test seam: feed one fetch's url+body through the same path the wrapper uses. */
export function __noteForTest(text: string): void { note(text); }

/** Watch a store reader and call `onChange` when the open event id CHANGES.
 *
 *  ⛔ MEASURED 2026-09-09 after the fetch wake-up shipped: on Parikara the
 *  match page arrives over the sportfeed WebSocket, never through a GetEvent
 *  fetch, so `note()` had nothing to see and the open match still waited for
 *  the 30s poll (26.1s measured). The app's redux store DOES fill within a
 *  second, so it is polled — an in-memory read every 500ms, no request. The
 *  lobby (no id) resets the watcher so re-opening the same match fires again. */
export function watchOpenEvent(
  read: () => string | null,
  onChange: (id: string) => void,
  intervalMs = 500,
): ReturnType<typeof setInterval> {
  let last = read() ?? "";
  return setInterval(() => {
    const now = read() ?? "";
    if (!now) { last = ""; return; }
    if (now === last) return;
    last = now;
    try { onChange(now); } catch { /* the watcher must never break the page */ }
  }, intervalMs);
}

function note(text: string): void {
  if (!EVENT_HINT.test(text)) return;
  const ids = extractEventIds(text);
  if (ids.length !== 1) return; // ambiguous payload: say nothing
  const changed = ids[0] !== lastId;
  lastId = ids[0];
  lastAt = Date.now();
  if (!changed || !listener) return;
  try { listener(lastId); } catch { /* the listener must never break the app's fetch */ }
}

/** Wrap fetch once. Never throws, never alters the operator's request. */
export function observeOpenMatch(): void {
  if (wrapped) return;
  try {
    const w = window as unknown as { __bcOpenMatch?: boolean; fetch: typeof fetch };
    if (w.__bcOpenMatch) return;
    const original = window.fetch;
    if (typeof original !== "function") return;
    w.__bcOpenMatch = true;
    wrapped = true;
    window.fetch = function (this: unknown, ...args: Parameters<typeof fetch>) {
      try {
        const input = args[0] as Request | string | URL;
        const url = String((input as Request)?.url ?? input);
        const body = args[1] && typeof (args[1] as RequestInit).body === "string"
          ? String((args[1] as RequestInit).body)
          : "";
        note(url + " " + body);
      } catch {
        /* a ruler must never break the thing it measures */
      }
      return original.apply(this as never, args);
    } as typeof fetch;
  } catch {
    /* no fetch to wrap — the DOM reader is still available */
  }
}

/** The observed open fixture, but ONLY if it is on the board we hold. */
export function openEventId(boardIds: Set<string>, now = Date.now()): string | null {
  if (!lastId || now - lastAt > FRESH_MS) return null;
  return boardIds.has(lastId) ? lastId : null;
}

/** Test seam. */
export function __setObserved(id: string, at: number): void {
  lastId = id;
  lastAt = at;
}

import { detectOpenMatch, BoardEntry } from "./open-match-dom";

/** The id of the match the OPERATOR APP itself says is open, or null.
 *
 * ⭐ ROUND 3, and the authoritative signal. Round 2 sent the raw observed
 * /Common/GetEvent id as context — and the app fires GetEvent for its own
 * FEATURED super-tip cards too: measured live in the lobby 2026-08-31, a frame
 * for Aston Villa–Arsenal arrived with no user action, and the widget pinned a
 * match nobody was looking at. Network traffic answers "what did the app
 * fetch", never "what is the user looking at".
 *
 * The app's redux store answers the real question: on a match page
 * `sportdigi.mobile.store.getState().eventDetails.eventList[0]` is the open
 * event (13 elements, the requested event first — same shape as GetEvent),
 * and it is EMPTY in the lobby. Measured both ways on Barcelona–Rayo
 * Vallecano. A bundle upgrade that reshapes the store fails CLOSED: every
 * step is optional-chained and any surprise returns null. */
export { appOpenEvent, appOpenEventId, appStoreReachable } from "./app-store";
import { appOpenEventId, appStoreReachable } from "./app-store";

/**
 * Which fixture is open: the operator app's own store first, then the
 * board-validated observations.
 *
 * ⛔ NOTHING UNVALIDATED LEAVES HERE EXCEPT THE STORE ID. The store id IS the
 * open match by the app's own routing, so when no client board can enrich it
 * (only `prematch_pull_share` percent of sessions — 5 on Parikara — hold a
 * client pre-match board) it goes out as a context-only event id and the
 * SERVER validates candidacy: the ranking pin only fires for an id that is a
 * real candidate on its live board or pre-match cache. A raw network id gets
 * no such pass — see appOpenEventId for the featured-card misfire that rule
 * exists to prevent. The net and DOM readers stay validated-only.
 */
export function resolveOpenMatch<T extends BoardEntry>(
  pool: T[],
  prematch: T[],
  doc?: Document,
  w?: unknown,
  now = Date.now(),
): { entry: T | null; contextOnlyId: string | null } {
  const combined = pool.concat(prematch);
  const byId = (id: string): T | null =>
    combined.find((m) => String(m.id ?? "") === id) ?? null;
  const appId = appOpenEventId(w);
  if (appId) {
    const entry = byId(appId);
    return entry ? { entry, contextOnlyId: null } : { entry: null, contextOnlyId: appId };
  }
  // ⛔ THE STORE'S "NOTHING OPEN" IS AN ANSWER. Measured on parikara 2026-09-09:
  // the football LOBBY carries a live-events row inside the operator's own
  // shadow DOM, and when exactly one fixture sits in it (Румыния–Латвия) the
  // DOM reader's "exactly one board pair on the page" rule declared it the
  // open match — the lobby pinned that card and showed ADD instead of OPEN
  // MATCH (owner's report). The store said the lobby was empty all along.
  // The net/DOM readers are for operators WITHOUT a store, not a second
  // opinion over one.
  if (appStoreReachable(w)) return { entry: null, contextOnlyId: null };
  const ids = new Set(combined.map((m) => String(m.id ?? "")));
  const foundId = openEventId(ids, now) || detectOpenMatch(combined, doc);
  if (foundId) {
    const entry = byId(foundId);
    if (entry) return { entry, contextOnlyId: null };
  }
  return { entry: null, contextOnlyId: null };
}
